import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { ProjectAssignmentPopover, ProjectPickerCommand } from './ProjectAssignmentMenu';

import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  type ProjectRow,
  useSaveToProject,
  useUserProjectsList,
} from '@/hooks/projects/useSaveToProject';
import { trackProductEvent } from '@/lib/analytics/product-events';

export interface SaveToProjectMenuProps {
  /** Optional artifact identifier — when provided, a project_files row is inserted on save. */
  artifactId?: string | null;
  /** Artifact kind for the project_files row. Defaults to 'message'. */
  artifactKind?: string;
  /** Optional chat session id — when provided, the session's project_id is updated on save. */
  chatSessionId?: string | null;
  /** Optional callback fired after the mutation resolves successfully. */
  onSaved?: (projectId: string, projectName: string) => void;
  /** Controlled open state (optional). Falls back to internal state when omitted. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Trigger node. Defaults to an outline Button labelled "Add to campaign". */
  children?: React.ReactNode;
}

export function SaveToProjectMenu({
  artifactId,
  artifactKind = 'message',
  chatSessionId,
  onSaved,
  open: openProp,
  onOpenChange,
  children,
}: SaveToProjectMenuProps): JSX.Element {
  const navigate = useNavigate();
  const [internalOpen, setInternalOpen] = useState(false);
  const open = openProp ?? internalOpen;
  const setOpen = onOpenChange ?? setInternalOpen;
  const [pendingId, setPendingId] = useState<string | null>(null);
  const { data: projects = [], isLoading: projectsLoading } = useUserProjectsList(open);
  const saveMutation = useSaveToProject();
  const mediaUuid =
    artifactKind === 'media' && artifactId && UUID_RE.test(artifactId) ? artifactId : null;

  if (mediaUuid && !chatSessionId) {
    return (
      <ProjectAssignmentPopover
        mediaIds={[mediaUuid]}
        open={openProp}
        onOpenChange={onOpenChange}
        onAssigned={onSaved}
      >
        {children ?? (
          <Button size="sm" variant="outline">
            Add to campaign
          </Button>
        )}
      </ProjectAssignmentPopover>
    );
  }

  const handleSelect = async (project: ProjectRow) => {
    setPendingId(project.id);
    void trackProductEvent('project_save_to_project_clicked', {
      project_id: project.id,
      artifact_kind: artifactKind,
      has_artifact_id: !!artifactId,
      has_chat_session_id: !!chatSessionId,
    });
    try {
      await saveMutation.mutateAsync({
        projectId: project.id,
        artifactId,
        artifactKind,
        chatSessionId,
      });
      const projectName = project.name || 'Untitled project';
      const target =
        artifactKind === 'media' ? `/projects/${project.id}?tab=media` : `/projects/${project.id}`;
      toast.success(`Added to ${projectName}`, {
        action: {
          label: 'Open project',
          onClick: () => navigate(target),
        },
      });
      onSaved?.(project.id, project.name);
      setOpen(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setPendingId(null);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {children ?? (
          <Button size="sm" variant="outline">
            Add to campaign
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[min(18rem,var(--radix-popover-content-available-width))] p-0"
      >
        <ProjectPickerCommand
          projects={projects}
          loading={projectsLoading}
          pendingProjectId={pendingId}
          onSelect={(projectId) => {
            const project = projects.find((p) => p.id === projectId);
            if (project) void handleSelect(project);
          }}
          onCreateProject={() => navigate('/?mode=plan')}
        />
      </PopoverContent>
    </Popover>
  );
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
